"use client";

import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Info, X } from "lucide-react";

type ToastKind = "success" | "error" | "info";

interface ToastItem {
  id: number;
  kind: ToastKind;
  title: string;
  message?: string;
}

interface ToastApi {
  success: (title: string, message?: string) => void;
  error: (title: string, message?: string) => void;
  info: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}

const kindStyle: Record<ToastKind, { icon: typeof Info; chip: string; bar: string; border: string }> = {
  success: { icon: CheckCircle2, chip: "bg-[#0e7a53]/12 text-[#0e7a53]", bar: "bg-[#0e7a53]", border: "border-[#0e7a53]/40" },
  error: { icon: AlertTriangle, chip: "bg-[#b3261e]/12 text-[#b3261e]", bar: "bg-[#b3261e]", border: "border-[#b3261e]/40" },
  info: { icon: Info, chip: "bg-gold-500/15 text-gold-700", bar: "bg-gold-500", border: "border-gold-500/50" },
};

const TOAST_MS = 4500;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, title: string, message?: string) => {
      const id = ++seq.current;
      setItems((prev) => [...prev.slice(-3), { id, kind, title, message }]);
      window.setTimeout(() => dismiss(id), TOAST_MS);
    },
    [dismiss]
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (t, m) => push("success", t, m),
      error: (t, m) => push("error", t, m),
      info: (t, m) => push("info", t, m),
    }),
    [push]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        role="region"
        aria-label="Notifications"
        className="pointer-events-none fixed right-4 top-5 z-[80] flex w-[calc(100vw-2rem)] max-w-[380px] flex-col gap-3 sm:right-6"
      >
        {items.map((t) => {
            const s = kindStyle[t.kind];
            const Icon = s.icon;
            return (
              <div
                key={t.id}
                className={`toast-in pointer-events-auto relative flex items-center gap-3.5 overflow-hidden rounded-2xl border-2 bg-white py-3.5 pl-3.5 pr-3 shadow-[0_18px_40px_-12px_rgba(38,34,31,0.35)] ${s.border}`}
                role="status"
              >
                <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${s.chip}`}>
                  <Icon className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold leading-tight text-ink">{t.title}</p>
                  {t.message && <p className="mt-0.5 text-[13px] leading-snug text-ink-soft">{t.message}</p>}
                </div>
                <button
                  type="button"
                  onClick={() => dismiss(t.id)}
                  aria-label="Dismiss notification"
                  className="self-start rounded-lg p-1 text-ink-muted transition-colors hover:bg-cream hover:text-ink"
                >
                  <X className="h-4 w-4" />
                </button>
                <span
                  className={`absolute bottom-0 left-0 h-[3px] w-full origin-left ${s.bar}`}
                  style={{ animation: `toast-progress ${TOAST_MS}ms linear forwards` }}
                  aria-hidden
                />
              </div>
            );
          })}
      </div>
    </ToastContext.Provider>
  );
}
